import LocalStorage from "./LocalStorage"
import Props from "../models/Properties"
import Account from "models/Account"

const PropsTemplate = [
    {
        _group: {
            title: ""
        },
        items: [
            {
                path: 'display_stats',
                title: 'Display Stats',
                type: 'bool',
            },
            {
                path: 'display_debug_lines',
                title: 'Display Debug Lines',
                type: 'bool',
            },
            {
                path: 'display_point_cloud',
                title: 'Display Point Cloud',
                type: 'bool',
            },
            {
                path: 'display_debug_planes',
                title: 'Display Debug Planes',
                type: 'bool',
            },
            {
                path: 'display_debug_physics',
                title: 'Display Debug Physics',
                type: 'bool',
            },
            {
                path: 'display_dummies',
                title: 'Display dummies',
                type: 'bool',
            },
            {
                path: 'dummies_as_boxes',
                title: 'Dummies as boxes (mob)',
                type: 'bool',
            },
            {
                path: 'display_occlusion_objects',
                title: 'Display Occlusion Objects',
                type: 'bool',
            },
            {
                path: 'display_markers',
                title: 'Display Markers',
                type: 'bool',
            },
            {
                path: 'display_shadows',
                title: 'Display Shadows',
                type: 'bool',
            },
            {
                path: 'display_anchors',
                title: 'Display Anchors',
                type: 'bool',
            },
            {
                path: 'anchor_search_radius',
                title: 'Anchor Search Radius (meters)',
                type: 'float',
                defaultValue: 15
            },
            {
                path: 'tracking_strategy',
                title: 'Tracking Strategy',
                type: 'enum',
                items: [
                    'NearestAnchor',
                    'NearestAnchorFixed',
                    'FirstAnchor',
                    'FirstAnchorFixed'
                ],
                defaultValue: 'NearestAnchor'
            },
            {
                path: 'ios_raycast_strategy',
                title: 'iOS Raycast Strategy',
                type: 'enum',
                items: [
                    'ExistingPlaneGeometry',
                    'ExistingPlaneInfinite',
                    'EstimatedPlane'
                ],
                defaultValue: 'EstimatedPlane'
            },
            {
                path: 'ios_plane_detection',
                title: 'iOS Plane Detection',
                type: 'bool',
                defaultValue: true
            },
            {
                path: 'display_logs',
                title: 'Display Logs',
                type: 'bool',
            }
        ]
    },
    {
        _group: {
            title: "Developer",
            developer_only: true,
            hide: true
        },
        items: [
            {
                path: 'gateway_service',
                title: 'Gateway Service',
                type: 'enum',
                items: [
                    'https://gateway.dimx.world:5001',
                    'http://localhost:5000',
                    'http://sergei-laptop:5000'
                ],
                defaultValue: 'https://gateway.dimx.world:5001'
            },
            {
                path: 'generic_service_override',
                title: 'Generic Service Override',
                type: 'enum',
                items: [
                    '',
                    'ws://localhost:8000',
                    'ws://sergei-laptop:5000'
                ],
                defaultValue: ''
            },
            {
                path: 'post_host',
                title: 'Post Host',
                type: 'enum',
                items: [
                    'https://api.dimx.world:9001',
                    'http://localhost:9000',
                    'http://sergei-laptop:9000'
                ],
                defaultValue: 'https://api.dimx.world:9001'
            },
            {
                path: 'web_app_host',
                title: 'Web App Host',
                type: 'enum',
                items: [
                    'https://meta.dimx.world',
                    'http://localhost:3010',
                    'http://sergei-laptop:3010'
                ],
                defaultValue: 'https://meta.dimx.world'
            },
            {
                path: 'code_editor_host',
                title: 'Code Editor Host',
                type: 'enum',
                items: [
                    'https://code.dimx.world',
                    'https://localhost:3021',
                    'https://sergei-laptop:3021'
                ],
                defaultValue: 'https://code.dimx.world'
            },
            {
                path: 'files_host',
                title: 'Files Host',
                type: 'enum',
                items: [
                    'https://files.dimx.world',
                    'http://localhost:3030',
                    'http://sergei-laptop:3030'
                ],
                defaultValue: 'https://files.dimx.world'
            },
            {
                path: 'home_dimension_override',
                title: 'Home Dimension Override',
                type: 'enum',
                items: [
                    '',
                    '3382710084', // KarmielDX
                    '1565156516', // EpsomDX
                ],
                defaultValue: ''
            },
        ]
    }
];

class Settings
{
    readonly STORAGE_PATH = '/LocalStorage/settings.json';

    //data: { [id: string] : any; } = {};
    data: any = {};
    properties: any[] = [];

    constructor() {
        this.data = LocalStorage.get(this.STORAGE_PATH);
        console.log(`Loaded settings: ${JSON.stringify(this.data)}`);

        if (!this.gatewayService()) {
            this.setGatewayService("https://gateway.dimx.world:5001");
        }

        if (!this.postHost()) {
            this.setPostHost("https://api.dimx.world:9001");
        }

        if (!this.codeEditorHost()) {
            this.setCodeEditorHost("https://code.dimx.world");
        }

        if (!this.filesHost()) {
            this.setFilesHost("https://files.dimx.world");
        }

        if (!this.anchorSearchRadius()) {
            this.setAnchorSearchRadius(15)
        }

        this.properties = Props.parse(PropsTemplate, this.data);
    }

    updateSettingsGroups(account: Account) {
        for (let item of this.properties) {
            if (item._group && item._group.developer_only) {
                item._group.hide = account.developer ? false : true;
            }
        }
    }

    private get(key: string): any {
        return this.data ? this.data[key] : null;
    }

    private set(key: string, value: any) {
        if (!this.data) {
            this.data = {};
        }
        this.data[key] = value;
         LocalStorage.set(this.STORAGE_PATH, this.data);
    }

    saveProps(props: any) {
        this.data = {...this.data, ...props};
        LocalStorage.set(this.STORAGE_PATH, this.data);
    }

    gatewayService() { return this.get('gateway_service'); }
    setGatewayService(value: string) { this.set('gateway_service', value); }
   
    webGenericService() { return this.get('web_generic_service'); }
    setWebGenericService(value: any) { this.set('web_generic_service', value); }

    postHost() { return this.get('post_host'); }
    setPostHost(value: string) { return this.set('post_host', value); }

    codeEditorHost() { return this.get('code_editor_host'); }
    setCodeEditorHost(value: string) { return this.set('code_editor_host', value); }

    filesHost() { return this.get('files_host'); }
    setFilesHost(value: string) { return this.set('files_host', value); }

    anchorSearchRadius() { return this.get('anchor_search_radius'); }
    setAnchorSearchRadius(value: number) { return this.set('anchor_search_radius', value); }

    genericServiceOverride() { return this.get('generic_service_override'); }
    setGenericServiceOverride(value: string) { return this.set('generic_service_override', value); }

    homeDimensionOverride() { return this.get('home_dimension_override'); }
    setHomeDimensionOverride(value: string) { return this.set('home_dimension_override', value); }

    makeFileUrl(dimension: string, env: string, file: string) {
        return `${this.filesHost()}/${dimension}/env/${env}/${file}`
    }
}

const g_settings= new Settings()
export default g_settings;
